/**
 * Período da tela da empresa no layout de 10/2026: um ou mais anos e um intervalo em
 * quinzenas dentro do ano (0 = 1ª de jan … 23 = 2ª de dez), com a periodicidade que
 * agrupa as colunas das demonstrações. A quinzena só existe em Demonstrações; as outras
 * abas leem o mesmo período em meses inteiros.
 */

export type Periodicidade =
  | 'quinzenal'
  | 'mensal'
  | 'bimestral'
  | 'trimestral'
  | 'semestral'
  | 'anual'
  | 'personalizado';

export const PERIODICIDADES: [Periodicidade, string][] = [
  ['quinzenal', 'Quinzenal'],
  ['mensal', 'Mensal'],
  ['bimestral', 'Bimestral'],
  ['trimestral', 'Trimestral'],
  ['semestral', 'Semestral'],
  ['anual', 'Anual'],
  ['personalizado', 'Personalizado'],
];

/** Tamanho de cada bloco, em quinzenas. */
export const TAMANHO: Record<Exclude<Periodicidade, 'personalizado'>, number> = {
  quinzenal: 1,
  mensal: 2,
  bimestral: 4,
  trimestral: 6,
  semestral: 12,
  anual: 24,
};

export const MESES_CURTOS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

export const MESES_LONGOS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

export interface IPeriodo {
  /** anos marcados, em ordem */
  anos: number[];
  /** primeira quinzena do intervalo, 0 a 23 */
  h1: number;
  /** última quinzena do intervalo, 0 a 23 */
  h2: number;
  tipo: Periodicidade;
}

const TIPOS = new Set(PERIODICIDADES.map(([k]) => k));
const doisDigitos = (n: number) => String(n).padStart(2, '0');
const ordenar = (anos: number[]) => Array.from(new Set(anos)).sort((a, b) => a - b);

/** Mês (0 a 11) e ano de "AAAA-MM". */
const partes = (competencia: string) => ({
  ano: Number(competencia.slice(0, 4)),
  mes: Number(competencia.slice(5, 7)) - 1,
});

/** "2026-09" da quinzena h do ano. */
export const competenciaDa = (ano: number, h: number): string =>
  `${ano}-${doisDigitos(Math.floor(h / 2) + 1)}`;

/** O intervalo começa ou termina no meio de um mês. */
export const temMeioDeMes = (p: Pick<IPeriodo, 'h1' | 'h2'>): boolean =>
  p.h1 % 2 === 1 || p.h2 % 2 === 0;

/** Último mês (0 a 11) com revisão em cada ano; -1 sem nenhum. */
export const ultimoMesPorAno = (meses: string[]): Record<number, number> => {
  const saida: Record<number, number> = {};
  meses.forEach(m => {
    const { ano, mes } = partes(m);
    saida[ano] = Math.max(saida[ano] ?? -1, mes);
  });
  return saida;
};

/**
 * Período a partir da URL. `de` e `comp` são o começo e o fim em meses no último ano
 * (o que as outras abas usam); `anos`, `q` ("h1-h2") e `ver` só aparecem quando fogem
 * do padrão. `q` que não bate com `de`/`comp` é ignorado.
 */
export const lerPeriodo = (
  params: { get: (chave: string) => string | null },
  de: string,
  competencia: string,
): IPeriodo => {
  const fim = partes(competencia);
  const ini = partes(de);
  const anos = ordenar(
    (params.get('anos') || '')
      .split(',')
      .map(Number)
      .filter(a => Number.isInteger(a) && a > 1990 && a <= fim.ano)
      .concat(fim.ano),
  );
  let h1 = ini.ano === fim.ano ? ini.mes * 2 : 0;
  let h2 = fim.mes * 2 + 1;
  const q = /^(\d{1,2})-(\d{1,2})$/.exec(params.get('q') || '');
  if (q) {
    const a = Number(q[1]);
    const b = Number(q[2]);
    if (a <= b && b <= 23 && Math.floor(a / 2) * 2 === h1 && Math.floor(b / 2) * 2 + 1 === h2) {
      h1 = a;
      h2 = b;
    }
  }
  const ver = params.get('ver') as Periodicidade | null;
  return { anos, h1, h2, tipo: ver && TIPOS.has(ver) ? ver : 'personalizado' };
};

/** Parâmetros da URL do período; `null` apaga o parâmetro. */
export const paraUrl = (p: IPeriodo): Record<string, string | null> => {
  const ano = p.anos[p.anos.length - 1];
  return {
    comp: competenciaDa(ano, p.h2),
    de: competenciaDa(ano, p.h1),
    anos: p.anos.length > 1 ? p.anos.join(',') : null,
    q: temMeioDeMes(p) ? `${p.h1}-${p.h2}` : null,
    ver: p.tipo === 'personalizado' ? null : p.tipo,
  };
};

/** O mesmo período em meses inteiros: o que as abas sem quinzena enxergam. */
export const emMesesInteiros = (p: IPeriodo): IPeriodo => ({
  ...p,
  h1: Math.floor(p.h1 / 2) * 2,
  h2: Math.floor(p.h2 / 2) * 2 + 1,
  tipo: p.tipo === 'quinzenal' ? 'mensal' : p.tipo,
});

const anosEmTexto = (anos: number[]) =>
  anos.length === 1
    ? String(anos[0])
    : `${anos.slice(0, -1).join(', ')} e ${anos[anos.length - 1]}`;
const quinzena = (h: number) => (h % 2 ? '2ª q. ' : '1ª q. ');
const mm = (h: number) => doisDigitos(Math.floor(h / 2) + 1);

/** Texto do botão: "09/2026", "07/2026 a 09/2026", "1ª q. 09/2026" ou "07 a 09 · 2025 e 2026". */
export const rotuloDoPeriodo = (p: IPeriodo): string => {
  const { anos, h1, h2 } = p;
  const umAno = anos.length === 1;
  if (temMeioDeMes(p)) {
    const faixa =
      h1 === h2
        ? `${quinzena(h1)}${mm(h1)}`
        : `${quinzena(h1)}${mm(h1)} a ${quinzena(h2)}${mm(h2)}`;
    return faixa + (umAno ? `/${anos[0]}` : ` · ${anosEmTexto(anos)}`);
  }
  if (umAno)
    return mm(h1) === mm(h2)
      ? `${mm(h1)}/${anos[0]}`
      : `${mm(h1)}/${anos[0]} a ${mm(h2)}/${anos[0]}`;
  return `${mm(h1) === mm(h2) ? mm(h1) : `${mm(h1)} a ${mm(h2)}`} · ${anosEmTexto(anos)}`;
};

/** Faixa por extenso, embaixo da grade: "jul — set · 2026" ou "1ª q. set · 2026". */
export const faixaDoPeriodo = (p: IPeriodo): string => {
  const { h1, h2 } = p;
  const mes = (h: number) => MESES_CURTOS[Math.floor(h / 2)];
  let faixa: string;
  if (temMeioDeMes(p) || p.tipo === 'quinzenal')
    faixa =
      h1 === h2
        ? `${quinzena(h1)}${mes(h1)}`
        : `${quinzena(h1)}${mes(h1)} — ${quinzena(h2)}${mes(h2)}`;
  else faixa = mes(h1) === mes(h2) ? mes(h1) : `${mes(h1)} — ${mes(h2)}`;
  return `${faixa} · ${anosEmTexto(p.anos)}`;
};

/** Bloco da periodicidade que contém a quinzena h, sem passar do último mês com dados. */
export const blocoDe = (tipo: Periodicidade, h: number, ultimoMes: number): [number, number] => {
  if (tipo === 'quinzenal' || tipo === 'personalizado') return [h, h];
  const tam = TAMANHO[tipo];
  const ini = Math.floor(h / tam) * tam;
  return [ini, Math.max(ini, Math.min(ini + tam - 1, ultimoMes * 2 + 1))];
};

/** Não deixa o fim passar do último mês com dados nos anos marcados. */
export const limitar = (p: IPeriodo, ultimos: Record<number, number>): IPeriodo => {
  const ultimo = Math.max(...p.anos.map(a => ultimos[a] ?? -1));
  if (ultimo < 0) return p;
  return { ...p, h2: Math.max(p.h1, Math.min(p.h2, ultimo * 2 + 1)) };
};

/**
 * Setas do botão: andam um bloco da periodicidade (ou o tamanho do intervalo, no
 * personalizado e na quinzena); com um ano só, passam para o ano vizinho. null quando
 * não há para onde ir.
 */
export const andar = (
  p: IPeriodo,
  passo: -1 | 1,
  ultimos: Record<number, number>,
): IPeriodo | null => {
  let a: number;
  let b: number;
  if (p.tipo === 'personalizado' || p.tipo === 'quinzenal') {
    const tam = p.h2 - p.h1 + 1;
    a = p.h1 + passo * tam;
    b = p.h2 + passo * tam;
  } else {
    const tam = TAMANHO[p.tipo];
    a = Math.floor(p.h1 / tam) * tam + passo * tam;
    b = a + tam - 1;
  }
  const comDados = (ano: number, h: number) => Math.floor(h / 2) <= (ultimos[ano] ?? -1);
  if (a >= 0 && b <= 23) {
    if (!p.anos.some(ano => comDados(ano, a))) return null;
    return limitar({ ...p, h1: a, h2: b }, ultimos);
  }
  if (p.anos.length > 1) return null;
  const ano = p.anos[0] + passo;
  const a2 = a - passo * 24;
  const b2 = b - passo * 24;
  if (a2 < 0 || b2 > 23 || !comDados(ano, a2)) return null;
  return limitar({ ...p, anos: [ano], h1: a2, h2: b2 }, ultimos);
};

/** Rascunho do painel: o período em edição e se o primeiro clique já marcou o início. */
export interface IRascunho extends IPeriodo {
  esperandoFim: boolean;
}

/**
 * Clique num mês (h1-h2 = as duas quinzenas) ou numa quinzena. No personalizado e na
 * quinzena, o primeiro clique marca o início e o segundo o fim; nas outras
 * periodicidades, o clique escolhe o bloco inteiro.
 */
export const escolher = (r: IRascunho, h1: number, h2: number, ultimoMes: number): IRascunho => {
  if (r.tipo === 'personalizado' || r.tipo === 'quinzenal') {
    if (!r.esperandoFim) return { ...r, h1, h2, esperandoFim: true };
    return { ...r, h1: Math.min(r.h1, h1), h2: Math.max(r.h2, h2), esperandoFim: false };
  }
  const [a, b] = blocoDe(r.tipo, h1, ultimoMes);
  return { ...r, h1: a, h2: b, esperandoFim: false };
};

/** Troca da periodicidade no painel: ajusta o intervalo ao bloco que contém o fim. */
export const trocarTipo = (r: IRascunho, tipo: Periodicidade, ultimoMes: number): IRascunho => {
  if (tipo === 'personalizado') return { ...r, tipo, esperandoFim: false };
  if (tipo === 'quinzenal') return { ...r, tipo, h1: r.h2, h2: r.h2, esperandoFim: false };
  const [a, b] = blocoDe(tipo, r.h2, ultimoMes);
  return { ...r, tipo, h1: a, h2: b, esperandoFim: false };
};

/** Atalhos do painel, contados a partir do último mês revisado da empresa. */
export const atalhos = (ultimos: Record<number, number>): [string, IPeriodo][] => {
  const anos = Object.keys(ultimos)
    .map(Number)
    .filter(a => ultimos[a] >= 0)
    .sort((a, b) => a - b);
  if (!anos.length) return [];
  const ano = anos[anos.length - 1];
  const fim = ultimos[ano] * 2 + 1;
  const desde = (meses: number) => Math.max(0, fim + 1 - meses * 2);
  return [
    ['Esta quinzena', { anos: [ano], h1: fim, h2: fim, tipo: 'quinzenal' }],
    ['Este mês', { anos: [ano], h1: fim - 1, h2: fim, tipo: 'mensal' }],
    ['Últimos 3 meses', { anos: [ano], h1: desde(3), h2: fim, tipo: 'personalizado' }],
    ['Últimos 6 meses', { anos: [ano], h1: desde(6), h2: fim, tipo: 'personalizado' }],
    ['Este ano', { anos: [ano], h1: 0, h2: fim, tipo: 'anual' }],
    ['Tudo', { anos, h1: 0, h2: 23, tipo: 'anual' }],
  ];
};
